import { Injectable } from '@angular/core';
import { User } from '../domain/user';

/**
 * PermissionService - Controle de Permissões Mobile
 *
 * Implementa controle de acesso baseado no sistema PermissionRoutes
 * que replica a estrutura do AuthRoutes.php do CakePHP.
 *
 * Funcionalidades:
 * - Validação de permissões por controller/action
 * - Suporte a perfis de usuário
 * - Restrições específicas por perfil (Ticket #5954)
 * - Cache local de permissões
 * - Controle de visibilidade de UI
 *
 * @author Douglas Nunes
 * @version 1.0
 * @since 2025-08-19
 */
@Injectable()
export class PermissionService {

    private permissionsMap: any = {
        'contratos': {
            'actions': ['index', 'cadastrar', 'get', 'getContratosAC', 'getContratosByClienteId', 'getDataSource', 'validarNrMedidor', 'validarCpfTitular', 'exibir', 'pesquisarContratos', 'alterarStatus'],
            'perfils': ['ADMINISTRADOR', 'CONCESSIONARIA', 'OPERADOR', 'OPERADOR RECICLAGEM', 'GRANDE GERADOR', 'SUPERVISOR RECICLAGEM', 'GERENTE', 'DIRETORIA'],
            'modulo': [1, 2, 3, 4],
            'soft_actions': ['getContratosAC', 'getContratosByClienteId', 'getDataSource', 'validarNrMedidor', 'validarCpfTitular', 'pesquisarContratos'],
            'restricted_actions': {
                'OPERADOR RECICLAGEM': ['index', 'get', 'getContratosAC', 'getContratosByClienteId', 'getDataSource', 'validarNrMedidor', 'validarCpfTitular', 'exibir', 'pesquisarContratos'] // Sem cadastrar e alterarStatus
            }
        },
        'clientes': {
            'actions': ['index', 'get', 'exibir', 'cadastrar', 'getClienteById', 'validarCpfNis', 'getClientesAC', 'getClienteByContrato', 'getDataSource'],
            'perfils': ['ADMINISTRADOR', 'OPERADOR', 'OPERADOR RECICLAGEM', 'GRANDE GERADOR', 'CONCESSIONARIA', 'SUPERVISOR RECICLAGEM', 'GERENTE', 'DIRETORIA', 'OPERADOR LOJA PNGD', 'COORDENADOR PNGD', 'SUPERVISOR PNGD', 'OPERADOR ADMINISTRATIVO PNGD'],
            'modulo': [1, 2, 3, 4],
            'soft_actions': ['getClienteById', 'validarCpfNis', 'getClientesAC', 'getClienteByContrato', 'getDataSource'],
            'restricted_actions': {
                'OPERADOR RECICLAGEM': ['index', 'get', 'exibir', 'getClienteById', 'getClienteByContrato', 'validarCpfNis'] // Sem cadastrar - Ticket #5954
            }
        },
        'reciclagem': {
            'actions': ['index', 'get', 'cadastrar', 'getReciclagem', 'getReciclagemById', 'exibir', 'envioEmLote', 'cancelarEnvioEmLote', 'alterarStatus', 'eventoStatus', 'enviarRecibo', 'enviarReciboVarios', 'adicionarParticipante', 'removerParticipante', 'reciclagens', 'emissaoRecibos', 'cancelarEmLote', 'gerarArquivoIntegracao', 'getReciclagens', 'alterar'],
            'perfils': ['ADMINISTRADOR', 'OPERADOR', 'OPERADOR RECICLAGEM', 'GRANDE GERADOR', 'SUPERVISOR RECICLAGEM', 'GERENTE', 'DIRETORIA', 'CONCESSIONARIA'],
            'modulo': [4],
            'restricted_actions': {
                'CONCESSIONARIA': ['getReciclagens']
            }
        },
        'eventos': {
            'actions': ['index', 'cadastrar', 'get', 'exibir', 'validarDataEvento', 'alterarStatus', 'getEvento', 'escalacao', 'getEventos', 'pesquisarEventos', 'checkEventoParceiro', 'getByParceiro', 'buscarEventos'],
            'perfils': ['ADMINISTRADOR', 'GERENTE', 'DIRETORIA', 'OPERADOR', 'OPERADOR RECICLAGEM', 'SUPERVISOR RECICLAGEM', 'GRANDE GERADOR', 'CONCESSIONARIA'],
            'modulo': [1, 2, 3, 4],
            'soft_actions': ['getEvento', 'getEventos', 'pesquisarEventos', 'checkEventoParceiro', 'buscarEventos', 'validarDataEvento'],
            'restricted_actions': {
                'OPERADOR RECICLAGEM': ['index', 'get', 'exibir', 'validarDataEvento', 'getEvento', 'getEventos', 'pesquisarEventos', 'checkEventoParceiro', 'buscarEventos']
            }
        },
        'participacao': {
            'actions': ['index', 'cadastrar', 'get', 'exibir', 'alterar', 'remover'],
            'perfils': ['ADMINISTRADOR', 'OPERADOR', 'OPERADOR RECICLAGEM', 'GRANDE GERADOR', 'SUPERVISOR RECICLAGEM', 'GERENTE', 'DIRETORIA'],
            'modulo': [4]
        },
        'vigencias': {
            'actions': ['index', 'cadastrar', 'get', 'validarDataInicial', 'getVigenciasPorRecurso', 'getVigenciaRecurso', 'getVigencia', 'getVigenciaByID', 'deletar', 'ativar', 'alterarStatus'],
            'perfils': ['ADMINISTRADOR', 'GERENTE', 'DIRETORIA', 'OPERADOR', 'OPERADOR RECICLAGEM', 'SUPERVISOR RECICLAGEM'],
            'modulo': [4],
            'soft_actions': ['getVigenciasPorRecurso', 'getVigenciaRecurso', 'getVigencia', 'getVigenciaByID', 'validarDataInicial']
        }
    };

    private userPermissions: any = null;
    private currentUser: User = null;

    constructor() {
        console.log('PermissionService initialized - Ticket #5954');
    }

    /**
     * Define o usuário atual e calcula suas permissões
     * @param user Usuário logado
     */
    setUser(user: User): void {
        this.currentUser = user;
        this.calculateUserPermissions();
    }

    /**
     * Calcula e armazena permissões do usuário atual
     */
    private calculateUserPermissions(): void {
        try {
            // Defensive check for user and profile
            if (!this.currentUser) {
                this.userPermissions = {};
                return;
            }

            if (!this.currentUser.perfil || !this.currentUser.perfil.nome) {
                this.userPermissions = {};
                return;
            }

            const userProfile = this.currentUser.perfil.nome;
            this.userPermissions = {};

            // Administrador e Operador têm acesso total no mobile (Ticket #5954)
            if (userProfile === 'ADMINISTRADOR' || userProfile === 'OPERADOR') {
                try {
                    for (const controller in this.permissionsMap) {
                        if (this.permissionsMap.hasOwnProperty(controller)) {
                            this.userPermissions[controller] = this.permissionsMap[controller].actions || ['*'];
                        }
                    }
                    return;
                } catch (error) {
                    // Fallback: dar acesso total mesmo com erro
                    this.userPermissions = { '*': ['*'] };
                    return;
                }
            }

            // Calcular permissões específicas do perfil
            try {
                for (const controller in this.permissionsMap) {
                    if (!this.permissionsMap.hasOwnProperty(controller)) continue;

                    const controllerPerms = this.permissionsMap[controller];

                    if (!controllerPerms || !Array.isArray(controllerPerms.perfils)) {
                        continue;
                    }

                    // Verificar se o perfil tem acesso ao controller
                    if (controllerPerms.perfils.includes(userProfile)) {
                        let allowedActions = Array.isArray(controllerPerms.actions) ? [...controllerPerms.actions] : [];

                        // Aplicar restrições específicas se existirem
                        if (controllerPerms.restricted_actions && controllerPerms.restricted_actions[userProfile] && Array.isArray(controllerPerms.restricted_actions[userProfile])) {
                            allowedActions = [...controllerPerms.restricted_actions[userProfile]];
                        }

                        this.userPermissions[controller] = allowedActions;
                    }
                }
            } catch (error) {
                this.userPermissions = {};
            }

        } catch (error) {
            console.info('[PermissionService] Erro geral ao calcular permissões:', error);
            this.userPermissions = {};
        }
    }

    /**
     * Verifica se o usuário tem permissão para executar uma ação
     * @param controller Nome do controller
     * @param action Nome da ação (opcional, padrão: 'index')
     * @returns boolean
     */
    hasPermission(controller: string, action: string = 'index'): boolean {
        try {
            if (!this.currentUser || !this.userPermissions) {
                return false;
            }

            // Verificação defensiva de perfil
            if (!this.currentUser.perfil || !this.currentUser.perfil.nome) {
                return false;
            }

            const controllerName = controller.toLowerCase();
            const userProfile = this.currentUser.perfil.nome;

            // Administrador e Operador sempre têm acesso no mobile (Ticket #5954)
            if (userProfile === 'ADMINISTRADOR' || userProfile === 'OPERADOR') {
                return true;
            }

            // Verificar se tem permissões para o controller
            if (!this.userPermissions[controllerName] || !Array.isArray(this.userPermissions[controllerName])) {
                return false;
            }

            const allowedActions = this.userPermissions[controllerName];

            // Verificar acesso total ou ação específica
            let hasAccess = allowedActions.includes('*') || allowedActions.includes(action);

            // Concessão contextual: quando o RBAC estático nega, uma regra dependente
            // de estado de runtime (ex.: flag cadastro_em_campo do evento aberto) ainda
            // pode conceder acesso. Espelha PermissionMiddleware::grantsContextualAccess.
            if (!hasAccess) {
                hasAccess = this.grantsContextualAccess(controllerName, action);
            }

            if (!hasAccess) {
                console.info('[PermissionService] Acesso negado -', userProfile, '->', controllerName + '.' + action);
                console.info('[PermissionService] Ações permitidas:', allowedActions.join(', '));
            } else {
                console.info('[PermissionService] Acesso concedido -', userProfile, '->', controllerName + '.' + action);
            }

            return hasAccess;
        } catch (error) {
            console.info('[PermissionService] Erro ao verificar permissão:', error, '- Controller:', controller, 'Action:', action);
            return false;
        }
    }

    /**
     * Despacha concessões contextuais (overrides do RBAC estático no mobile).
     *
     * Ponto único de extensão equivalente ao PermissionMiddleware::grantsContextualAccess
     * do backend: quando o mapa estático nega uma ação, decide se alguma regra
     * dependente de estado de runtime concede acesso. Cada caso é explícito e
     * delega a um resolver dedicado.
     *
     * @param controller Nome do controller (já em minúsculas)
     * @param action Ação solicitada
     * @returns boolean
     */
    private grantsContextualAccess(controller: string, action: string): boolean {
        // ECOENEL-258: Operador Reciclagem cadastra contrato em campo quando o evento
        // aberto tem a flag cadastro_em_campo. O backend revalida e audita.
        if (controller === 'contratos' && action === 'cadastrar') {
            return this.permiteCadastroContratoEmCampo();
        }

        // Próximas concessões contextuais entram aqui (1 caso = 1 if + 1 resolver).

        return false;
    }

    /**
     * ECOENEL-258: indica se o evento aberto habilita o cadastro de contrato em
     * campo para o Operador Reciclagem. Lê a flag de evento_aberto no localStorage,
     * fonte usada pelo restante do app (contrato-form, contrato-search).
     *
     * @returns boolean
     */
    private permiteCadastroContratoEmCampo(): boolean {
        try {
            if (!this.isCurrentUserOperadorReciclagem()) {
                return false;
            }

            const raw = localStorage.getItem('evento_aberto');
            if (!raw) {
                return false;
            }

            const evento = JSON.parse(raw);
            return !!(evento && evento.cadastro_em_campo);
        } catch (error) {
            console.info('[PermissionService] Erro ao avaliar cadastro em campo:', error);
            return false;
        }
    }

    /**
     * Verifica se o usuário pode cadastrar (create/store)
     * @param controller Nome do controller
     * @returns boolean
     */
    canCreate(controller: string): boolean {
        return this.hasPermission(controller, 'cadastrar') || this.hasPermission(controller, 'store');
    }

    /**
     * Verifica se o usuário pode editar (update)
     * @param controller Nome do controller
     * @returns boolean
     */
    canEdit(controller: string): boolean {
        return this.hasPermission(controller, 'alterar') || this.hasPermission(controller, 'update');
    }

    /**
     * Verifica se o usuário pode deletar (destroy)
     * @param controller Nome do controller
     * @returns boolean
     */
    canDelete(controller: string): boolean {
        return this.hasPermission(controller, 'deletar') || this.hasPermission(controller, 'destroy');
    }

    /**
     * Verifica se o usuário pode visualizar (index/show)
     * @param controller Nome do controller
     * @returns boolean
     */
    canView(controller: string): boolean {
        return this.hasPermission(controller, 'index') || this.hasPermission(controller, 'exibir');
    }

    /**
     * Obtém todas as permissões do usuário atual
     * @returns object
     */
    getUserPermissions(): any {
        return this.userPermissions || {};
    }

    /**
     * Obtém restrições específicas para um controller
     * @param controller Nome do controller
     * @returns object
     */
    getControllerRestrictions(controller: string): any {
        if (!this.currentUser || !this.currentUser.perfil) {
            return { hasRestrictions: false, allowedActions: [], blockedActions: [] };
        }

        const controllerName = controller.toLowerCase();
        const userProfile = this.currentUser.perfil.nome;
        const controllerPerms = this.permissionsMap[controllerName];

        if (!controllerPerms) {
            return { hasRestrictions: true, allowedActions: [], blockedActions: ['*'] };
        }

        // Verificar se há restrições específicas
        if (controllerPerms.restricted_actions && controllerPerms.restricted_actions[userProfile]) {
            const allowedActions = controllerPerms.restricted_actions[userProfile];
            const allActions = controllerPerms.actions || [];
            const blockedActions = allActions.filter(action => !allowedActions.includes(action));

            return {
                hasRestrictions: true,
                allowedActions: allowedActions,
                blockedActions: blockedActions
            };
        }

        return {
            hasRestrictions: false,
            allowedActions: controllerPerms.actions || [],
            blockedActions: []
        };
    }

    /**
     * Verifica se uma ação é crítica para o ticket #5954
     * @param controller Nome do controller
     * @param action Nome da ação
     * @returns boolean
     */
    isCriticalAction(controller: string, action: string): boolean {
        const criticalActions = {
            'contratos': ['cadastrar', 'alterarStatus'],
            'clientes': ['cadastrar'],
            'usuarios': ['cadastrar', 'alterar']
        };

        const controllerName = controller.toLowerCase();
        return criticalActions[controllerName] && criticalActions[controllerName].includes(action);
    }

    /**
     * Obtém informações do perfil atual
     * @returns object
     */
    getCurrentUserProfile(): any {
        try {
            if (!this.currentUser) {
                return null;
            }

            if (!this.currentUser.perfil || !this.currentUser.perfil.nome) {
                return {
                    nome: 'Sem perfil',
                    id: null,
                    isOperadorReciclagem: false,
                    isAdmin: false,
                    isOperador: false
                };
            }

            return {
                nome: this.currentUser.perfil.nome,
                id: this.currentUser.perfil.id,
                isOperadorReciclagem: this.currentUser.perfil.nome === 'OPERADOR RECICLAGEM',
                isAdmin: this.currentUser.perfil.nome === 'ADMINISTRADOR',
                isOperador: this.currentUser.perfil.nome === 'OPERADOR'
            };
        } catch (error) {
            console.info('[PermissionService] Erro ao obter perfil atual:', error);
            return null;
        }
    }

    /**
     * Filtra lista de itens baseado em permissões
     * @param items Lista de itens
     * @param controller Controller relacionado
     * @param actionField Campo que contém a ação (opcional)
     * @returns Array filtrado
     */
    filterByPermissions(items: any[], controller: string, actionField?: string): any[] {
        try {
            if (!items || !Array.isArray(items) || items.length === 0) {
                return items || [];
            }

            return items.filter(item => {
                try {
                    const action = actionField && item && item[actionField] ? item[actionField] : 'index';
                    return this.hasPermission(controller, action);
                } catch (error) {
                    console.info('[PermissionService] Erro ao filtrar item:', error);
                    return false;
                }
            });
        } catch (error) {
            console.info('[PermissionService] Erro ao filtrar array por permissões:', error);
            return [];
        }
    }

    /**
     * Gera log de auditoria para ações críticas
     * @param controller Nome do controller
     * @param action Nome da ação
     * @param allowed Se foi permitido ou negado
     */
    logPermissionCheck(controller: string, action: string, allowed: boolean): void {
        if (this.isCriticalAction(controller, action)) {
            const logData = {
                timestamp: new Date().toISOString(),
                user: this.currentUser ? this.currentUser.nome : 'Unknown',
                perfil: this.currentUser && this.currentUser.perfil ? this.currentUser.perfil.nome : 'Unknown',
                controller: controller,
                action: action,
                allowed: allowed,
                ticket: '#5954'
            };

            console.log('[PERMISSION AUDIT] ' + (allowed ? 'ALLOWED' : 'DENIED') + ':', logData);
        }
    }

    /**
     * Helper para verificar se usuário é OPERADOR RECICLAGEM (compatível com TS antigo)
     */
    isCurrentUserOperadorReciclagem(): boolean {
        try {
            return this.currentUser &&
                   this.currentUser.perfil &&
                   this.currentUser.perfil.nome === 'OPERADOR RECICLAGEM';
        } catch (error) {
            console.info('[PermissionService] Erro ao verificar se é operador reciclagem:', error);
            return false;
        }
    }

    /**
     * Helper para verificar se usuário é ADMINISTRADOR
     */
    isCurrentUserAdmin(): boolean {
        try {
            return this.currentUser &&
                   this.currentUser.perfil &&
                   this.currentUser.perfil.nome === 'ADMINISTRADOR';
        } catch (error) {
            console.info('[PermissionService] Erro ao verificar se é administrador:', error);
            return false;
        }
    }

    /**
     * Helper para verificar se usuário é OPERADOR
     */
    isCurrentUserOperador(): boolean {
        try {
            return this.currentUser &&
                   this.currentUser.perfil &&
                   this.currentUser.perfil.nome === 'OPERADOR';
        } catch (error) {
            console.info('[PermissionService] Erro ao verificar se é operador:', error);
            return false;
        }
    }

    /**
     * Helper para obter nome do perfil (compatível com TS antigo)
     */
    getCurrentUserProfileName(): string {
        try {
            if (this.currentUser && this.currentUser.perfil && this.currentUser.perfil.nome) {
                return this.currentUser.perfil.nome;
            }
            return 'Sem perfil';
        } catch (error) {
            console.info('[PermissionService] Erro ao obter nome do perfil:', error);
            return 'Sem perfil';
        }
    }

    /**
     * Mapeia controller de navegação para controller de validação de permissões
     * Resolve divergência entre @cakephp/ (reciclagem) e @back/ (participacao)
     * Ticket #5954 - Aderência entre sistemas
     *
     * @param string navController Controller usado na navegação/API
     * @returns string Controller usado para validação de permissões
     */
    mapNavigationToPermissionController(navController: string): string {
        const mappings = {
            // Navegação 'participacao' usa permissões de 'reciclagem' (AuthRoutes.php padrão)
            'participacao': 'reciclagem',
            // Outros mapeamentos futuros podem ser adicionados aqui
        };

        return mappings[navController] || navController;
    }

    /**
     * Verifica permissão considerando mapeamento de controllers
     * Método wrapper que automaticamente aplica mapeamento quando necessário
     *
     * @param navController Controller usado na navegação
     * @param action Ação a ser verificada
     * @returns boolean
     */
    hasNavigationPermission(navController: string, action: string = 'index'): boolean {
        try {
            const permissionController = this.mapNavigationToPermissionController(navController);
            return this.hasPermission(permissionController, action);
        } catch (error) {
            console.info('[PermissionService] Erro ao verificar permissão de navegação:', error);
            return false;
        }
    }

    /**
     * Verifica se pode criar considerando mapeamento de controllers
     *
     * @param navController Controller usado na navegação
     * @returns boolean
     */
    canNavigationCreate(navController: string): boolean {
        const permissionController = this.mapNavigationToPermissionController(navController);
        return this.canCreate(permissionController);
    }

    /**
     * Verifica se pode editar considerando mapeamento de controllers
     *
     * @param navController Controller usado na navegação
     * @returns boolean
     */
    canNavigationEdit(navController: string): boolean {
        const permissionController = this.mapNavigationToPermissionController(navController);
        return this.canEdit(permissionController);
    }
}



// WEBPACK FOOTER //
// ./src/providers/permission-service.ts